import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { useLanguage } from '../contexts/LanguageContext';
import { Trash2, Upload, Plus, ImageIcon } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export const Settings = () => {
  const { t } = useLanguage();
  const [rate, setRate] = useState(25000);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Size Guide Images
  const [sizeGuideImages, setSizeGuideImages] = useState([]);
  const [loadingSizeImages, setLoadingSizeImages] = useState(true);
  const [savingSizeImages, setSavingSizeImages] = useState(false);
  const [imageUrlInput, setImageUrlInput] = useState('');
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/api/settings`, { withCredentials: true });
      setRate(response.data.eur_vnd_rate);
    } catch (error) {
      console.error('Error fetching settings:', error);
      toast.error('Erreur lors du chargement des paramètres');
    } finally {
      setLoading(false);
    }
  };

  const fetchSizeGuideImages = async () => {
    try {
      const res = await axios.get(`${API}/api/settings/size-guide-images`, { withCredentials: true });
      setSizeGuideImages(res.data.images || []);
    } catch (e) { console.error('Error fetching size guide images:', e); }
    finally { setLoadingSizeImages(false); }
  };

  useEffect(() => {
    fetchSettings();
    fetchSizeGuideImages();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.put(`${API}/api/settings`, { eur_vnd_rate: parseFloat(rate) }, { withCredentials: true });
      toast.success('Paramètres enregistrés');
    } catch (error) {
      toast.error("Erreur lors de l'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  const handleSaveSizeGuideImages = async () => {
    setSavingSizeImages(true);
    try {
      await axios.put(`${API}/api/settings/size-guide-images`, { images: sizeGuideImages }, { withCredentials: true });
      toast.success(t('images_saved'));
    } catch (e) {
      toast.error('Erreur');
    } finally {
      setSavingSizeImages(false);
    }
  };

  const handleAddImageUrl = () => {
    if (!imageUrlInput.trim()) return;
    if (sizeGuideImages.length >= 4) { toast.error(t('max_images_reached')); return; }
    setSizeGuideImages(prev => [...prev, imageUrlInput.trim()]);
    setImageUrlInput('');
    toast.success(t('image_added'));
  };

  const handleRemoveImage = (idx) => {
    setSizeGuideImages(prev => prev.filter((_, i) => i !== idx));
    toast.success(t('image_removed'));
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (sizeGuideImages.length >= 4) { toast.error(t('max_images_reached')); return; }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post(`${API}/api/settings/size-guide-images/upload`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      const url = `${API}${res.data.url}`;
      setSizeGuideImages(prev => [...prev, url]);
      toast.success(t('image_added'));
    } catch (err) {
      toast.error('Erreur upload');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-96">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900 mx-auto" />
          <p className="mt-4 text-sm text-slate-600">Chargement...</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-semibold text-slate-900 tracking-tight">{t('settings_title')}</h1>
          <p className="text-sm text-slate-600 mt-1">Configuration du système</p>
        </div>

        {/* Exchange Rate */}
        <Card className="border-slate-200 shadow-sm max-w-2xl">
          <CardHeader>
            <CardTitle className="text-lg font-medium">{t('eur_vnd_rate')}</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="rate">{t('eur_vnd_rate')}</Label>
                <Input
                  data-testid="exchange-rate-input"
                  id="rate"
                  type="number"
                  step="0.01"
                  min="0"
                  value={rate}
                  onChange={(e) => setRate(e.target.value)}
                  required
                  className="max-w-md"
                />
                <p className="text-xs text-slate-500">1 EUR = {parseFloat(rate).toLocaleString()} VND</p>
              </div>
              <Button data-testid="save-settings-button" type="submit" className="bg-slate-900 hover:bg-slate-800" disabled={saving}>
                {saving ? 'Enregistrement...' : t('save')}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Size Guide Images */}
        <Card className="border-slate-200 shadow-sm max-w-2xl">
          <CardHeader>
            <CardTitle className="text-lg font-medium flex items-center gap-2">
              <ImageIcon size={20} />
              {t('size_guide_images')}
            </CardTitle>
            <p className="text-sm text-slate-500">{t('size_guide_images_desc')}</p>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Current images */}
            {!loadingSizeImages && sizeGuideImages.length > 0 && (
              <div className="grid grid-cols-2 gap-3">
                {sizeGuideImages.map((url, idx) => (
                  <div key={`img-${url.slice(-20)}-${idx}`} className="relative border border-slate-200 rounded-lg overflow-hidden group">
                    <img
                      data-testid={`settings-size-image-${idx}`}
                      src={url}
                      alt={`Size guide ${idx + 1}`}
                      className="w-full h-32 object-cover"
                      onError={(e) => { e.target.src = ''; e.target.alt = 'Image not found'; }}
                    />
                    <button
                      data-testid={`remove-size-image-${idx}`}
                      onClick={() => handleRemoveImage(idx)}
                      className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <Trash2 size={14} />
                    </button>
                    <p className="text-[10px] text-slate-400 px-2 py-1 truncate">{url.split('/').pop()}</p>
                  </div>
                ))}
              </div>
            )}

            {sizeGuideImages.length < 4 && (
              <>
                {/* Add by URL */}
                <div className="flex gap-2">
                  <Input
                    data-testid="size-guide-url-input"
                    value={imageUrlInput}
                    onChange={(e) => setImageUrlInput(e.target.value)}
                    placeholder="https://..."
                    className="flex-1"
                  />
                  <Button data-testid="add-image-url-btn" variant="outline" onClick={handleAddImageUrl} disabled={!imageUrlInput.trim()}>
                    <Plus size={14} className="mr-1" /> {t('add_image_url')}
                  </Button>
                </div>

                {/* Upload file */}
                <div>
                  <input ref={fileInputRef} type="file" accept="image/*" onChange={handleFileUpload} className="hidden" />
                  <Button
                    data-testid="upload-size-image-btn"
                    variant="outline"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploading}
                  >
                    <Upload size={14} className="mr-1" /> {uploading ? '...' : t('upload_file')}
                  </Button>
                </div>
              </>
            )}

            {/* Save button */}
            <Button
              data-testid="save-size-guide-images"
              onClick={handleSaveSizeGuideImages}
              className="bg-slate-900 hover:bg-slate-800"
              disabled={savingSizeImages}
            >
              {savingSizeImages ? '...' : t('save')}
            </Button>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};
